/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import { datadogRum } from "@datadog/browser-rum";
import styled from "styled-components";

import * as API from "../../../../api/billing";
import AdjustRewardBalanceModalBody from "./adjustRewardBalanceModalBody";
import CatalogOverrideModalBody from "./catalogOverrideModalBody";
import Button from "../../../reusable/button";
import CSVExportModalBody from "./csvExportModalBody";
import Card from "../../../reusable/cardv2";
import CollapsibleTable from "../../../reusable/reactTable";
import ErrorMessage from "../../../reusable/errorMessage";
import InvoiceDownloadIcon from "../../../reusable/invoiceDownloadIcon";
import Modal from "../../../reusable/reactModal";
import RewardCSVDownloadIcon from "../../../reusable/rewardCSVDownloadIcon";
import { RewardBalanceData, RewardBalancePropTypes } from "../lib/types";
import { StyledErrorContainer } from "../lib/styles";
import {
  defaultRewardBalanceData,
  genericErrorMessage,
  rewardBalanceTableHeaders,
} from "../lib/constants";
import CatalogShutoffModal from "./catalogShutoffModal";

const StyledContainer = styled.div`
  position: relative;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 24px;
  width: 100%;
  justify-content: right;
`;

const StyledAdminButton = styled(Button)`
  margin-right: 25px;
`;

const StyledButton = styled(Button)`
  margin-right: 25px;
`;

const RewardBalanceCard = ({
  showSuperAdminFeature,
  updateToastMessage,
}: RewardBalancePropTypes): React.ReactElement => {
  const [data, setData] = React.useState<RewardBalanceData>(
    defaultRewardBalanceData
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [modal, setModal] = React.useState<string>("");
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [overrideButtonText, setOverrideButtonText] = React.useState<string>(
    ""
  );
  const [overrideModalTitle, setOverrideModalTitle] = React.useState<string>(
    ""
  );

  const closeModal = (): void => {
    return setModal("");
  };

  const fetchData = React.useCallback(
    async (loadFirstPage?: boolean) => {
      try {
        setLoading(true);
        const response = await API.getRewardBalance({
          page_number: loadFirstPage ? 1 : pageNumber,
          page_size: rowsPerPage,
        });

        setData({
          lineItems: response.data.data,
          ...response.data.meta,
        });
      } catch (error) {
        datadogRum.addError(error);
        setData({
          ...defaultRewardBalanceData,
          errorMessage: genericErrorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, rowsPerPage]
  );

  const fetchRewardGroupingData = async (paymentId: string): Promise<void> => {
    try {
      const lineItem = data.lineItems.find(
        (lineItem) => lineItem.id === paymentId
      );
      if (lineItem && (!lineItem.rewards || lineItem.rewards.length === 0)) {
        const response = await API.getRewardBalanceRewardGrouping({
          payment_id: paymentId,
        });
        const rewards = response.data.data || [];
        const updatedLineItems = data.lineItems.map((lineItem) => {
          if (lineItem.id === paymentId) {
            return {
              ...lineItem,
              rewards,
            };
          }
          return lineItem;
        });

        setData({
          ...data,
          lineItems: updatedLineItems,
        });
      }
    } catch (error) {
      datadogRum.addError(error);

      // NOTE: If an error occurs set to empty array so page will load
      const updatedLineItems = data.lineItems.map((lineItem) => {
        if (lineItem.id === paymentId) {
          return {
            ...lineItem,
            rewards: [],
          };
        }
        return lineItem;
      });

      setData({
        ...data,
        lineItems: updatedLineItems,
      });
    }
  };

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    if (data.overrideDate) {
      setOverrideButtonText(
        i18n.translate("admin_pages.activity.catalog_override.button.modify")
      );
      setOverrideModalTitle(
        i18n.translate("admin_pages.activity.catalog_override.modal.modify")
      );
    } else {
      setOverrideButtonText(
        i18n.translate("admin_pages.activity.catalog_override.button.add")
      );
      setOverrideModalTitle(
        i18n.translate("admin_pages.activity.catalog_override.modal.add")
      );
    }
  }, [data.overrideDate]);

  const handlePaginationChange = (value): void => {
    setPageNumber(value);
  };

  const handleRowsPerPageChange = (event): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const rowData = data.lineItems.map((item, index) => {
    if (item.type === "rewards") {
      item.downloadIcon = (
        <RewardCSVDownloadIcon
          paymentId={item.id}
          testId={`download-button-${index}`}
          updateToastMessage={updateToastMessage}
        />
      );
    }

    if (item.type === "payment" && item.invoiceId) {
      item.downloadIcon = (
        <InvoiceDownloadIcon
          invoiceId={item.invoiceId}
          testId={`download-button-${index}`}
          updateToastMessage={updateToastMessage}
        />
      );
    }
    return item;
  });

  const buttons = (
    <StyledButtonContainer>
      {showSuperAdminFeature && (
        <StyledAdminButton
          onClick={(): void => {
            setModal("catalogShutoff");
          }}
          size="small"
          testId="catalog-shutoff"
          variant="superAdmin"
        >
          {data.catalogShutoff ? "Turn Rewards On" : "Turn Rewards Off"}
        </StyledAdminButton>
      )}
      {showSuperAdminFeature && (
        <StyledAdminButton
          onClick={(): void => {
            setModal("catalogOverride");
          }}
          size="small"
          testId="catalog-override-button"
          variant="superAdmin"
        >
          {overrideButtonText}
        </StyledAdminButton>
      )}
      {showSuperAdminFeature && (
        <StyledAdminButton
          onClick={(): void => {
            setModal("adjustAwardBalance");
          }}
          size="small"
          testId="adjust-reward-balance-button"
          variant="superAdmin"
        >
          {i18n.translate("admin_pages.activity.adjust_reward_balance")}
        </StyledAdminButton>
      )}
      <StyledButton
        onClick={(): void => {
          setModal("exportCSV");
        }}
        size="small"
        testId="export-as-csv-button"
        variant="primary"
      >
        {i18n.translate("admin_pages.activity.export_as_csv")}
      </StyledButton>
    </StyledButtonContainer>
  );

  return (
    <Card
      bodyPadding="16px 0px 0px"
      testId="reward-balance"
      title={i18n.translate("admin_pages.activity.reward_balance")}
      buttons={buttons}
    >
      {data.errorMessage ? (
        <StyledErrorContainer>
          <ErrorMessage
            errorMessage={data.errorMessage}
            testId="reward-balance-error"
          />
        </StyledErrorContainer>
      ) : (
        <StyledContainer>
          <CollapsibleTable
            collapsibleRowDataAccessor="rewards"
            fetchCollabsibleData={fetchRewardGroupingData}
            handlePaginationChange={handlePaginationChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
            headers={rewardBalanceTableHeaders}
            loading={loading}
            pageNumber={pageNumber}
            rowCount={data.rowCount}
            rows={rowData}
            rowsPerPage={rowsPerPage}
          />
        </StyledContainer>
      )}
      <Modal
        ariaLabel="export csv modal"
        close={closeModal}
        showDialog={modal === "exportCSV"}
        testId="export-csv-modal"
        title={`${i18n.translate(
          "admin_pages.activity.export_csv_modal.title"
        )}`}
      >
        <CSVExportModalBody
          closeModal={closeModal}
          updateToastMessage={updateToastMessage}
        />
      </Modal>
      <Modal
        ariaLabel="adjust reward balance modal"
        close={closeModal}
        showDialog={modal === "adjustAwardBalance"}
        testId="adjust-reward-balance-modal"
        title={`${i18n.translate(
          "admin_pages.activity.adjust_reward_balance_modal.title"
        )}`}
      >
        <AdjustRewardBalanceModalBody
          closeModal={closeModal}
          fetchData={fetchData}
        />
      </Modal>
      <Modal
        ariaLabel="catalog override modal"
        close={closeModal}
        showDialog={modal === "catalogOverride"}
        testId="catalog-override-modal"
        title={overrideModalTitle}
      >
        <CatalogOverrideModalBody
          closeModal={closeModal}
          catalogStatus={data.catalogStatus}
          fetchData={fetchData}
          overrideDate={data.overrideDate}
          catalogShutoff={data.catalogShutoff}
        />
      </Modal>
      <Modal
        ariaLabel="catalog shutoff modal"
        close={closeModal}
        showDialog={modal === "catalogShutoff"}
        testId="catalog-override-modal"
        title="Catalog Status Confirmation"
      >
        <CatalogShutoffModal
          closeModal={closeModal}
          fetchData={fetchData}
          companyId={data.companyId}
          catalogShutoff={data.catalogShutoff}
          updateToastMessage={updateToastMessage}
        />
      </Modal>
    </Card>
  );
};

export default RewardBalanceCard;
